import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import { logger, authLogger } from '../utils/logger';
import { sessionService } from '../services/api';
import type { AppStore, User, ChatMessage, ChatStatus, ChatStatusInfo, NavigationHistory } from '../types';

// Clean up any existing chat data from localStorage
const cleanupChatData = () => {
  try {
    const stored = localStorage.getItem('fabrik28-app-store');
    if (stored) {
      const parsed = JSON.parse(stored);
      if (parsed.state && (parsed.state.chatMessages || parsed.state.chatSessionId)) {
        // Remove chat data from stored state but preserve status for UX
        delete parsed.state.chatMessages;
        delete parsed.state.chatSessionId;
        // Reset chat status fields to initial state
        parsed.state.chatStatus = 'inactive';
        parsed.state.unreadCount = 0;
        parsed.state.lastMessageTimestamp = null;
        localStorage.setItem('fabrik28-app-store', JSON.stringify(parsed));
      }
    }
  } catch (error) {
    logger.warn('Failed to cleanup chat data from localStorage', { error });
  }
};

// Run cleanup on module load
cleanupChatData();

const useAppStore = create<AppStore>()(
  persist(
    (set, get) => ({
      // Theme state
      isDarkMode: false,
      toggleTheme: () => {
        const newMode = !get().isDarkMode;
        set({ isDarkMode: newMode });
        
        // Update document class for Tailwind dark mode
        if (newMode) {
          document.documentElement.classList.add('dark');
        } else {
          document.documentElement.classList.remove('dark');
        }
      },
      
      // Chat state - always starts fresh on page load
      isChatOpen: false,
      isChatMinimized: false,
      chatMessages: [],
      chatSessionId: null,
      isTyping: false,
      chatStatus: 'inactive' as ChatStatus,
      unreadCount: 0,
      lastMessageTimestamp: null,
      
      // Chat actions
      openChat: () => {
        set((state) => {
          // Mark messages as read when opening chat
          const updatedMessages = state.chatMessages.map(msg => ({ ...msg, isRead: true }));
          return {
            isChatOpen: true,
            isChatMinimized: false,
            chatMessages: updatedMessages,
            unreadCount: 0,
            chatStatus: state.chatMessages.length > 0 ? 'active' as ChatStatus : 'inactive' as ChatStatus
          };
        });
      },
      closeChat: () => set({ 
        isChatOpen: false, 
        isChatMinimized: false,
        chatStatus: 'inactive' as ChatStatus,
        chatMessages: [], // Clear messages when closing chat
        chatSessionId: null, // Reset session ID
        unreadCount: 0,
        lastMessageTimestamp: null,
      }),
      toggleChat: () => set((state) => ({ isChatOpen: !state.isChatOpen })),
      minimizeChat: () => set({ isChatMinimized: true }),
      restoreChat: () => {
        set((state) => {
          // Mark messages as read when restoring chat
          const updatedMessages = state.chatMessages.map(msg => ({ ...msg, isRead: true }));
          return {
            isChatMinimized: false,
            chatMessages: updatedMessages,
            unreadCount: 0,
            chatStatus: state.chatMessages.length > 0 ? 'active' as ChatStatus : 'inactive' as ChatStatus
          };
        });
      },
      toggleMinimizeChat: () => {
        set((state) => {
          if (state.isChatMinimized) {
            // Restoring from minimize - mark messages as read
            const updatedMessages = state.chatMessages.map(msg => ({ ...msg, isRead: true }));
            return {
              isChatMinimized: false,
              chatMessages: updatedMessages,
              unreadCount: 0,
              chatStatus: state.chatMessages.length > 0 ? 'active' as ChatStatus : 'inactive' as ChatStatus
            };
          } else {
            // Minimizing
            return { isChatMinimized: true };
          }
        });
      },
      
      addMessage: (message: Omit<ChatMessage, 'id' | 'timestamp'>) => set((state) => {
        const newMessage = {
          ...message,
          id: Date.now() + Math.random(),
          timestamp: new Date().toISOString(),
          isRead: state.isChatOpen && !state.isChatMinimized, // Mark as read if chat is open and visible
        };
        
        const updatedMessages = [...state.chatMessages, newMessage];
        const isAssistantMessage = message.role === 'assistant' && !message.isWelcome;
        const shouldIncrementUnread = isAssistantMessage && (!state.isChatOpen || state.isChatMinimized);
        
        return {
          chatMessages: updatedMessages,
          lastMessageTimestamp: newMessage.timestamp,
          unreadCount: shouldIncrementUnread ? state.unreadCount + 1 : state.unreadCount,
          chatStatus: shouldIncrementUnread ? 'new_messages' as ChatStatus : 
                     updatedMessages.length > 0 ? 'active' as ChatStatus : 'inactive' as ChatStatus
        };
      }),
      
      setChatSessionId: (sessionId: string) => set({ chatSessionId: sessionId }),
      setIsTyping: (isTyping: boolean) => set({ isTyping }),
      
      clearChat: () => set({
        chatMessages: [],
        chatSessionId: null,
        isTyping: false,
        chatStatus: 'inactive' as ChatStatus,
        unreadCount: 0,
        lastMessageTimestamp: null,
      }),
      
      // Chat status actions
      setChatStatus: (status: ChatStatus) => set({ chatStatus: status }),
      
      markMessagesAsRead: () => set((state) => ({
        chatMessages: state.chatMessages.map(msg => ({ ...msg, isRead: true })),
        unreadCount: 0,
        chatStatus: state.chatMessages.length > 0 ? 'active' as ChatStatus : 'inactive' as ChatStatus
      })),
      
      incrementUnreadCount: () => set((state) => ({
        unreadCount: state.unreadCount + 1,
        chatStatus: 'new_messages' as ChatStatus
      })),
      
      getChatStatusInfo: (): ChatStatusInfo => {
        const state = get();
        return {
          status: state.chatStatus,
          unreadCount: state.unreadCount,
          lastMessageTimestamp: state.lastMessageTimestamp,
          hasActiveSession: state.chatSessionId !== null && state.chatMessages.length > 0
        };
      },
      
      // Auth state
      isAuthenticated: false,
      user: null,
      
      // Auth actions
      login: (userData: User) => {
        // Validate user data
        if (!userData || !userData.id || !userData.email) {
          authLogger.error('Invalid user data provided to login', { userData });
          return;
        }
        
        // Update state
        set({
          isAuthenticated: true,
          user: userData,
        });
        
        // Store in session storage for persistence
        try {
          sessionStorage.setItem('isAuthenticated', 'true');
          sessionStorage.setItem('user', JSON.stringify(userData));
        } catch (error) {
          authLogger.error('Error storing auth state in session storage', { error });
        }
      },
      
      logout: async () => {
        // Sign out from Cognito to clear tokens
        try {
          await sessionService.signOut();
        } catch (error) {
          authLogger.error('Error signing out from Cognito:', error);
        }
        
        // Update state
        set({
          isAuthenticated: false,
          user: null,
          registrationData: null, // Clear registration data on logout
          pendingEmailVerification: false, // Clear pending verification on logout
          isVerificationFromLogin: false, // Clear login verification flag on logout
          // Close all auth-related modals
          isLoginModalOpen: false,
          isRegistrationModalOpen: false,
          // Clear any related flags
          showExistingUserMessage: false,
          prefillEmail: '',
          // Clear chat state
          chatMessages: [],
          chatSessionId: null,
          chatStatus: 'inactive' as ChatStatus,
          unreadCount: 0,
          lastMessageTimestamp: null,
        });
        
        // Clear session storage
        try {
          sessionStorage.removeItem('isAuthenticated');
          sessionStorage.removeItem('user');
          sessionStorage.removeItem('temp_registration_password');
        } catch (error) {
          authLogger.error('Error clearing auth state from session storage', { error });
        }

        // Clear persisted state in localStorage
        try {
          localStorage.removeItem('fabrik28-app-store');
        } catch (error) {
          authLogger.error('Error clearing persisted state from localStorage', { error });
        }
      },
      
      // Registration flow state
      registrationData: null,
      pendingEmailVerification: false,
      isVerificationFromLogin: false, // New flag specifically for login-to-verification flow
      showExistingUserMessage: false, // New flag for showing existing user message in login modal
      prefillEmail: '', // Email to prefill in login modal when redirected from registration
      setRegistrationData: (data) => set({ registrationData: data }),
      clearRegistrationData: () => set({ registrationData: null, pendingEmailVerification: false, isVerificationFromLogin: false, showExistingUserMessage: false, prefillEmail: '' }),
      setPendingEmailVerification: (pending) => set({ pendingEmailVerification: pending }),
      setVerificationFromLogin: (fromLogin) => set({ isVerificationFromLogin: fromLogin }),
      setShowExistingUserMessage: (show) => set({ showExistingUserMessage: show }),
      setPrefillEmail: (email) => set({ prefillEmail: email }),
      
      // Navigation history and form persistence for legal pages
      navigationHistory: {
        previousRoute: null,
        shouldShowBackButton: false,
        savedFormData: null
      },
      setNavigationHistory: (history) => set({ navigationHistory: history }),
      clearNavigationHistory: () => set({ 
        navigationHistory: { 
          previousRoute: null, 
          shouldShowBackButton: false, 
          savedFormData: null 
        } 
      }),
      
      // Modal state
      isLoginModalOpen: false,
      isEmailVerificationModalOpen: false,
      isRegistrationModalOpen: false,
      isForgotPasswordModalOpen: false,
      
      openLoginModal: () => set({ isLoginModalOpen: true }),
      openLoginModalClean: () => set({ isLoginModalOpen: true, showExistingUserMessage: false, prefillEmail: '' }),
      closeLoginModal: () => set({ isLoginModalOpen: false }),
      openEmailVerificationModal: () => set({ isEmailVerificationModalOpen: true }),
      closeEmailVerificationModal: () => set({ isEmailVerificationModalOpen: false }),
      openRegistrationModal: () => set({ isRegistrationModalOpen: true }),
      closeRegistrationModal: () => set({ isRegistrationModalOpen: false }),
      openForgotPasswordModal: () => set({ isForgotPasswordModalOpen: true }),
      closeForgotPasswordModal: () => set({ isForgotPasswordModalOpen: false }),
    }),
    {
      name: 'fabrik28-app-store',
      partialize: (state) => ({
        // Only persist theme and auth state, not chat history
        // Chat starts fresh on each page load for marketing context
        isDarkMode: state.isDarkMode,
        isAuthenticated: state.isAuthenticated,
        user: state.user,
        chatMessages: state.chatMessages,
        chatSessionId: state.chatSessionId,
        isChatMinimized: state.isChatMinimized, // Persist minimize state
        chatStatus: state.chatStatus, // Persist chat status
        unreadCount: state.unreadCount, // Persist unread count
        lastMessageTimestamp: state.lastMessageTimestamp, // Persist last message timestamp
        registrationData: state.registrationData,
        // Note: pendingEmailVerification and isVerificationFromLogin are NOT persisted - they should only be active during the current session
      }),
    }
  )
);

export default useAppStore;
